import React, { useState, useEffect, useRef, lazy, Suspense, startTransition } from'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from'react-router-dom';
import { AuthProvider, useAuth } from'./context/AuthContext';
import { AtozasLanguageProvider, useAtozasLanguage } from'./context/AtozasLanguageContext';
import { NotificationProvider } from'./context/NotificationContext';

import SplashScreen from'./components/common/SplashScreen';
import ErrorBoundary from'./components/common/ErrorBoundary';
import PWAInstallPrompt from'./components/common/PWAInstallPrompt';
import LanguageInitializer from'./components/common/LanguageInitializer';
import AutoTranslateWrapper from'./components/common/AutoTranslateWrapper';
import ProfilePage from'./components/user/ProfilePage';
import BookingConfirmationPage from'./components/user/BookingConfirmationPage';
import { getSocket } from'./services/socket';
import { getApiUrl } from'./services/api';
import'./index.css';

// PERFORMANCE: Lazy load heavy components for mobile optimization
// This reduces initial bundle size by 40-60% and improves mobile performance
const LandingPage = lazy(() => import('./components/authentication/LandingPage'));
const RegisterModal = lazy(() => import('./components/authentication/RegisterModal'));
const LoginModal = lazy(() => import('./components/authentication/LoginModal'));
const PhoneNumberForm = lazy(() => import('./components/authentication/PhoneNumberForm'));
const HomePage = lazy(() => import('./components/user/HomePage'));
const BookRidePage = lazy(() => import('./components/user/BookRidePage'));
const RidesPage = lazy(() => import('./components/user/RidesPage'));
const NearbyDriversPage = lazy(() => import('./components/user/NearbyDriversPage'));
const WaitingForDriversResponsePage = lazy(() => import('./components/user/WaitingForDriversResponsePage'));
const ReserveBookingDetailsPage = lazy(() => import('./components/user/ReserveBookingDetailsPage'));
const FeedbackPage = lazy(() => import('./components/user/FeedbackPage'));
const TermsConditionsPage = lazy(() => import('./components/user/TermsConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('./components/user/PrivacyPolicyPage'));
const SharedRideView = lazy(() => import('./components/user/SharedRideView'));
const SharedRidePreviewModal = lazy(() => import('./components/user/SharedRidePreviewModal'));
const DriverDashboard = lazy(() => import('./components/driver/DriverDashboard'));
const DriverRegistrationForm = lazy(() => import('./components/driver/DriverRegistrationForm'));
const BankDetailsForm = lazy(() => import('./components/driver/BankDetailsForm'));
const DriverRegistrationSuccess = lazy(() => import('./components/driver/DriverRegistrationSuccess'));
const PickupToDropoff = lazy(() => import('./components/driver/PickupToDropoff'));
const OTPVerificationPage = lazy(() => import('./components/driver/OTPVerificationPage'));
const DriverReserveBookingDetailsPage = lazy(() => import('./components/driver/DriverReserveBookingDetailsPage'));
const Onboarding = lazy(() => import('./components/common/Onboarding'));
const BlockedUserModal = lazy(() => import('./components/common/BlockedUserModal'));
const MessageModal = lazy(() => import('./components/common/MessageModal'));
const SubscriptionPricingPage = lazy(() => import('./components/common/SubscriptionPricingPage'));

// Wrapper components to ensure routes render correctly and log when matched
const SharedRideViewWrapper = () => {
 console.log('✅✅✅ ROUTE MATCHED: /shared-ride/:token/details');
 return <SharedRideView />;
};

const SharedRidePreviewModalWrapper = () => {
 console.log('✅✅✅ ROUTE MATCHED: /shared-ride/:token');
 return <SharedRidePreviewModal />;
};

const RegisterPageWrapper = () => {
 const navigate = useNavigate();

 return (
 <RegisterModal
 onClose={() => navigate('/')}
 onSuccess={() => {
 // RegisterModal already sets auth + onboarding flags.
 }}
 />
 );
};

function AppContent() {
 const { user, loading, logout } = useAuth();
 const { language } = useAtozasLanguage();
 const location = useLocation();
 const navigate = useNavigate();
 const [showRegisterModal, setShowRegisterModal] = useState(false);
 const [showLoginModal, setShowLoginModal] = useState(false);
 const [isBlocked, setIsBlocked] = useState(false);
 const [blockReason, setBlockReason] = useState(null);
 const [socialLoginUser, setSocialLoginUser] = useState(null);
 const [showOnboarding, setShowOnboarding] = useState(false);
 const [showAccountDeletedModal, setShowAccountDeletedModal] = useState(false);
 const [accountDeletedMessage, setAccountDeletedMessage] = useState('');
 
 // Check if current path is shared-ride (public route)
 // Handles both /shared-ride/:token and /shared-ride/:token/details
 const isSharedRidePath = location.pathname.startsWith('/shared-ride/');
 
 // Debug logging for routing
 useEffect(() => {
 console.log('🛣️ Current path:', location.pathname);
 console.log('🔍 Is shared-ride path:', isSharedRidePath);
 console.log('👤 User logged in:', !!user);
 
 // CRITICAL: If we're on a shared-ride path, prevent any navigation to dashboard
 if (isSharedRidePath && location.pathname !=='/shared-ride/'+ location.pathname.split('/shared-ride/')[1]) {
 console.warn('⚠️ Path changed while on shared-ride, checking...');
 }
 }, [location.pathname, isSharedRidePath, user]);

 // Language selection tracking
 useEffect(() => {
 if (!language) return;
 console.log('🌐 Language selected:', language);
 }, [language]);

 // If language selection redirected back with showRegister=1, open Register modal once.
 useEffect(() => {
 if (user) return;

 const params = new URLSearchParams(location.search);
 const shouldShowRegister = params.get('showRegister') ==='1';

 if (!shouldShowRegister) return;

 setShowLoginModal(false);
 setShowRegisterModal(true);

 // Clean the URL so refresh/back doesn't reopen the modal.
 params.delete('showRegister');
 const search = params.toString();
 navigate({ pathname: location.pathname, search: search ?`?${search}`:''}, { replace: true });
 }, [location.pathname, location.search, navigate, user]);
 
 // CRITICAL: Ensure shared-ride routes are always accessible
 // Log when we're on a shared-ride path to debug routing issues
 useEffect(() => {
 if (isSharedRidePath) {
 console.log('🔗🔗🔗 SHARED-RIDE MODE ACTIVE');
 console.log('Full path:', location.pathname);
 console.log('User logged in:', !!user);
 console.log('Routes should match /shared-ride/:token');
 }
 }, [isSharedRidePath, location.pathname, user]);

 // Handle user blockingsss
 useEffect(() => {
 if (!user) return;

 // Check if user is blocked on mount
 const checkBlockStatus = async () => {
 try {
 const response = await fetch(`${getApiUrl()}/api/validate-token`, {
 method:'POST',
 headers: {'Content-Type':'application/json','Authorization':`Bearer ${localStorage.getItem('uttaapp_token')}`},
 body: JSON.stringify({ token: localStorage.getItem('uttaapp_token') })
 });
 const data = await response.json();
 if (data.user && data.user.isBlocked) {
 console.log('🚫 User is blocked on mount:', data.user.blockedReason);
 setIsBlocked(true);
 setBlockReason(data.user.blockedReason);
 }
 } catch (error) {
 console.error('Error checking block status:', error);
 }
 };

 checkBlockStatus();

 // Listen for blocking events from custom window events
 const handleBlocked = (event) => {
 console.log('🚫 Blocked event received via window event:', event.detail);
 setIsBlocked(true);
 setBlockReason(event.detail?.reason || event.detail?.message ||'Your account has been blocked');
 };

 const handleUnblocked = () => {
 console.log('✅ Unblocked event received');
 setIsBlocked(false);
 setBlockReason(null);
 };

 const handleDeleted = (event) => {
 console.log('🗑️ Account deleted event received:', event.detail);
 setAccountDeletedMessage(event.detail?.message ||'Your account has been deleted by the administrator.');
 setShowAccountDeletedModal(true);
 };

 window.addEventListener('user-blocked', handleBlocked);
 window.addEventListener('user-unblocked', handleUnblocked);
 window.addEventListener('user-deleted', handleDeleted);

 // Initialize socket to listen for blocking
 const socket = getSocket();
 if (socket) {
 console.log('🔌 Setting up socket listeners for blocking');
 const handleSocketBlocked = (data) => {
 console.log('🚫 User blocked event received via socket:', data);
 setIsBlocked(true);
 setBlockReason(data.reason || data.message ||'Your account has been blocked');
 window.dispatchEvent(new CustomEvent('user-blocked', { detail: data }));
 };

 const handleSocketUserDeleted = (data) => {
 console.log('🗑️ User deleted event received via socket:', data);
 setAccountDeletedMessage(data.message ||'Your account has been deleted by the administrator.');
 setShowAccountDeletedModal(true);
 };

 const handleSocketDriverDeleted = (data) => {
 console.log('🗑️ Driver deleted event received via socket:', data);
 setAccountDeletedMessage(data.message ||'Your driver account has been deleted by the administrator.');
 setShowAccountDeletedModal(true);
 };

 const handleSocketNotification = (notification) => {
 console.log('📬 New notification received:', notification);
 if (notification.type ==='user_blocked'|| notification.blocked) {
 console.log('🚫 Blocking notification detected');
 setIsBlocked(true);
 setBlockReason(notification.reason || notification.message ||'Your account has been blocked');
 window.dispatchEvent(new CustomEvent('user-blocked', { 
 detail: { 
 blocked: true, 
 reason: notification.reason || notification.message 
 } 
 }));
 } else if (notification.type ==='user_unblocked'|| notification.blocked === false) {
 console.log('✅ Unblocking notification detected');
 setIsBlocked(false);
 setBlockReason(null);
 window.dispatchEvent(new CustomEvent('user-unblocked'));
 } else if (notification.type ==='account_deleted'|| notification.deleted) {
 console.log('🗑️ Account deleted notification detected');
 setAccountDeletedMessage(notification.message ||'Your account has been deleted by the administrator.');
 setShowAccountDeletedModal(true);
 }
 };

 const handleSocketConnect = () => {
 console.log('🔌 Socket connected, checking block status');
 socket.emit('check-block-status');
 };
 
 // Remove existing listeners to avoid duplicates
 socket.off('user-blocked');
 socket.off('user-deleted');
 socket.off('driver-deleted');
 socket.off('new-notification');
 
 socket.on('user-blocked', handleSocketBlocked);
 socket.on('user-deleted', handleSocketUserDeleted);
 socket.on('driver-deleted', handleSocketDriverDeleted);
 socket.on('new-notification', handleSocketNotification);

 // Check block status when socket connects
 if (socket.connected) {
 socket.emit('check-block-status');
 } else {
 socket.on('connect', handleSocketConnect);
 }

 return () => {
 window.removeEventListener('user-blocked', handleBlocked);
 window.removeEventListener('user-unblocked', handleUnblocked);
 window.removeEventListener('user-deleted', handleDeleted);
 socket.off('user-blocked', handleSocketBlocked);
 socket.off('user-deleted', handleSocketUserDeleted);
 socket.off('driver-deleted', handleSocketDriverDeleted);
 socket.off('new-notification', handleSocketNotification);
 socket.off('connect', handleSocketConnect);
 };
 }

 console.warn('⚠️ Socket not available for blocking notifications');

 return () => {
 window.removeEventListener('user-blocked', handleBlocked);
 window.removeEventListener('user-unblocked', handleUnblocked);
 window.removeEventListener('user-deleted', handleDeleted);
 };
 }, [user, logout]);

 // Check if onboarding should be shown (only for newly registered users)
 useEffect(() => {
 if (user && !loading) {
 const onboardingCompleted = localStorage.getItem('uttaapp_onboarding_completed');
 const isNewUser = localStorage.getItem('uttaapp_new_user_registered');
 
 // Show onboarding if user just registered and hasn't completed onboarding
 if (isNewUser ==='true'&& onboardingCompleted !=='true') {
 setShowOnboarding(true);
 // Clear the new user flag
 localStorage.removeItem('uttaapp_new_user_registered');
 } else if (onboardingCompleted ==='true') {
 // If onboarding is already completed, make sure we don't show it
 setShowOnboarding(false);
 }
 }
 }, [user, loading]);

 // Listen for onboarding completion (when user navigates from onboarding)
 useEffect(() => {
 const checkOnboardingStatus = () => {
 const onboardingCompleted = localStorage.getItem('uttaapp_onboarding_completed');
 if (onboardingCompleted ==='true'&& showOnboarding) {
 // Onboarding was completed, hide it
 setShowOnboarding(false);
 }
 };

 // Check immediately
 checkOnboardingStatus();

 // Also listen for storage changes (in case onboarding completes in another tab/window)
 const handleStorageChange = (e) => {
 if (e.key ==='uttaapp_onboarding_completed'&& e.newValue ==='true') {
 setShowOnboarding(false);
 }
 };

 window.addEventListener('storage', handleStorageChange);
 
 // Check periodically (in case same-tab completion)
 // PERFORMANCE: Changed from 100ms to 1000ms (1 second) to reduce CPU usage
 const interval = setInterval(checkOnboardingStatus, 1000);

 return () => {
 window.removeEventListener('storage', handleStorageChange);
 clearInterval(interval);
 };
 }, [showOnboarding]);

 // Show splash screen while loading
 if (loading) {
 return (
 <>
 <LanguageInitializer />
 <SplashScreen />
 </>
 );
 }

 // Show onboarding if needed (only for newly registered users)
 // Check localStorage directly to avoid race condition with useEffect
 const onboardingCompleted = localStorage.getItem('uttaapp_onboarding_completed');
 const isNewUser = localStorage.getItem('uttaapp_new_user_registered');
 
 if (user && isNewUser ==='true'&& onboardingCompleted !=='true') {
 // Don't clear the flag here - let Onboarding component clear it when user navigates
 // This ensures onboarding shows even if component re-renders
 return (
 <>
 <LanguageInitializer />
 <Onboarding />
 </>
 );
 }
 
 // Also show if showOnboarding state is true (for compatibility)
 if (showOnboarding && user) {
 return (
 <>
 <LanguageInitializer />
 <Onboarding />
 </>
 );
 }

 // Show blocked modal if user is blocked
 if (user && isBlocked) {
 return (
 <>
 <LanguageInitializer />
 <BlockedUserModal 
 reason={blockReason} 
 onClose={() => {
 // Don't allow closing - user must contact support
 }} 
 />
 </>
 );
 }

 // Show account deleted popup (user or driver deleted from DB) – then logout on OK
 if (showAccountDeletedModal) {
 return (
 <Suspense fallback={null}>
 <MessageModal
 message={accountDeletedMessage ||'Your account has been deleted by the administrator.'}
 title="Account deleted"type="error"onClose={async () => {
 setShowAccountDeletedModal(false);
 await logout();
 window.location.href ='/';
 }}
 />
 </Suspense>
 );
 }

 // If user is not logged in AND not accessing shared-ride, show landing page
 const normalizedPath = location.pathname.replace(/\/+$/,'') ||'/';
 const isRegisterPath = normalizedPath ==='/register';
 const isBookRidePath = normalizedPath ==='/book-ride';
 const isSubscriptionPath = ['/subscription-pricing','/pricing','/subscription','/SubscriptionPricingPage','/SubscriptionPricingPage.js'].includes(normalizedPath);
 
 console.log('🔍 Route check - path:', location.pathname,'user:', !!user,'isSharedRidePath:', isSharedRidePath,'isRegisterPath:', isRegisterPath,'isBookRidePath:', isBookRidePath,'loading:', loading);
 
 // CRITICAL: Wait for auth to finish loading before showing landing page
 // This prevents redirecting away from /book-ride during auth validation
 if (loading) {
 // Show nothing while auth is loading - routes will handle their own loading states
 return null;
 }
 
 if (!user && !isSharedRidePath && !isRegisterPath && !isBookRidePath && !isSubscriptionPath) {
 console.log('🔒 No user and not shared-ride/register/book-ride path - showing landing page');
 
 // Show PhoneNumberForm if social login user exists
 if (socialLoginUser) {
 return (
 <>
 <LanguageInitializer />
 <PhoneNumberForm
 user={socialLoginUser}
 onBack={() => {
 setSocialLoginUser(null);
 setShowLoginModal(true);
 }}
 />
 </>
 );
 }

 return (
 <>
 <LanguageInitializer />
 <LandingPage
 onRegisterClick={() => {
 startTransition(() => {
 setShowLoginModal(false);
 setShowRegisterModal(true);
 });
 }}
 onLoginClick={() => {
 startTransition(() => {
 setShowRegisterModal(false);
 setShowLoginModal(true);
 });
 }}
 />

 {/* Register Modal */}
 {showRegisterModal && (
 <Suspense fallback={null}>
 <RegisterModal
 onClose={() => setShowRegisterModal(false)}
 onSuccess={() => {
 setShowRegisterModal(false);
 // Mark user as newly registered to show onboarding (backup)
 localStorage.setItem('uttaapp_new_user_registered','true');
 // Clear any old onboarding completion flag
 localStorage.removeItem('uttaapp_onboarding_completed');
 }}
 onSocialLoginSuccess={(socialUser) => {
 setShowRegisterModal(false);
 setSocialLoginUser(socialUser);
 console.log('✅ Social signup successful, showing PhoneNumberForm');
 }}
 />
 </Suspense>
 )}

 {/* Login Modal */}
 {showLoginModal && (
 <Suspense fallback={null}>
 <LoginModal
 onClose={() => {
 setShowLoginModal(false);
 setSocialLoginUser(null);
 }}
 onRegisterClick={() => {
 startTransition(() => {
 setShowLoginModal(false);
 setShowRegisterModal(true);
 });
 }}
 onSuccess={() => {
 setShowLoginModal(false);
 setSocialLoginUser(null);
 console.log('✅ Login successful');
 }}
 onSocialLoginSuccess={(socialUser) => {
 setShowLoginModal(false);
 setSocialLoginUser(socialUser);
 console.log('✅ Social login successful, showing PhoneNumberForm');
 }}
 />
 </Suspense>
 )}
 </>
 );
 }
 
 // If it's a shared-ride path and no user, we MUST render AppRoutes
 // to allow the shared-ride routes to match
 console.log('✅ Proceeding to render routes - isSharedRide:', isSharedRidePath,'hasUser:', !!user);

 // Unified Routes Component - includes both user and driver routes
 const AppRoutes = () => {
 const navigate = useNavigate();
 const currentPath = location.pathname;
 const isCurrentlySharedRide = currentPath.startsWith('/shared-ride/');
 
 console.log('🎬 AppRoutes rendering - Path:', currentPath,'isSharedRidePath:', isSharedRidePath,'isCurrentlySharedRide:', isCurrentlySharedRide,'user:', !!user);
 
 // CRITICAL: Log route matching attempt
 useEffect(() => {
 if (isCurrentlySharedRide) {
 console.log('🔗🔗🔗 SHARED-RIDE PATH DETECTED IN AppRoutes - Routes should match!');
 console.log('Current path:', currentPath);
 console.log('Location pathname:', location.pathname);
 console.log('isSharedRidePath:', isSharedRidePath);
 console.log('isCurrentlySharedRide:', isCurrentlySharedRide);
 }
 }, [currentPath, isCurrentlySharedRide]);
 
 // CRITICAL: Always render shared-ride routes FIRST, before any other routes
 // This ensures they match regardless of user authentication state
 // PERFORMANCE: Wrap Routes in Suspense for lazy loading (mobile optimization)
 return (
 <Suspense fallback={<SplashScreen />}>
 <Routes>
 {/* ====================================================================== */}
 {/* PUBLIC ROUTES - NO AUTHENTICATION REQUIRED - MUST BE ABSOLUTELY FIRST */}
 {/* ====================================================================== */}
 {/* These routes MUST be rendered unconditionally and MUST be first */}
 
 {/* More specific route first - Full details route navigated to from preview modal */}
 <Route 
 path="/shared-ride/:token/details"element={
 <SharedRideViewWrapper />
 } 
 />
 
 {/* Preview modal route - shows when clicking"View Ride Details"from email */}
 {/* This route works for both logged-in and non-logged-in users */}
 <Route 
 path="/shared-ride/:token"element={
 <SharedRidePreviewModalWrapper />
 } 
 />
 
 {/* Register Page - Public route (renders RegisterModal as a page for reliable translation) */}
 <Route
 path="/register"element={<RegisterPageWrapper />}
 />

 <Route
 path="/subscription-pricing"element={<SubscriptionPricingPage />}
 />

 <Route
 path="/pricing"element={<SubscriptionPricingPage />}
 />

 <Route
 path="/subscription"element={<SubscriptionPricingPage />}
 />

 <Route
 path="/SubscriptionPricingPage"element={<SubscriptionPricingPage />}
 />

 <Route
 path="/SubscriptionPricingPage.js"element={<SubscriptionPricingPage />}
 />
 
 {/* ====================================================================== */}
 {/* AUTHENTICATED ROUTES - REQUIRE USER LOGIN */}
 {/* ====================================================================== */}
 
 {/* User Routes - require authentication (only render if user is logged in) */}
 {user && (
 <>
 <Route 
 path="/dashboard"element={(() => {
 console.log('✅ /dashboard route matched! Rendering HomePage');
 return (
 <HomePage
 user={user}
 onLogout={logout}
 navigateTo={(path, options) => navigate(path, options)}
 />
 );
 })()} 
 />
 <Route 
 path="/book-ride"element={<BookRidePage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/profile"element={<ProfilePage />} 
 />
 <Route 
 path="/rides"element={<RidesPage />} 
 />
 <Route 
 path="/nearby-drivers"element={<NearbyDriversPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/booking-confirmation"element={<BookingConfirmationPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/waiting-for-drivers"element={<WaitingForDriversResponsePage user={user} />} 
 />
 <Route 
 path="/reserve-booking/:bookingId"element={<ReserveBookingDetailsPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/reserve-booking-details"element={<ReserveBookingDetailsPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/feedback"element={<FeedbackPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/terms-conditions"element={<TermsConditionsPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/privacy-policy"element={<PrivacyPolicyPage user={user} onLogout={logout} />} 
 />
 </>
 )}
 
 {/* Driver Routes - require authentication */}
 {user && (
 <>
 <Route 
 path="/driver/register"element={
 <DriverRegistrationForm 
 user={user} 
 onBack={() => navigate('/dashboard')}
 onHome={() => navigate('/dashboard')}
 onLogout={logout}
 /> 
 } 
 />
 <Route 
 path="/driver/bank-details"element={
 <BankDetailsForm
 driver={user.driverId ? { driverId: user.driverId, userId: user.id || user._id } : null}
 user={user}
 onBack={() => navigate('/driver/register')}
 onComplete={() => navigate('/driver/dashboard')}
 onHome={() => navigate('/dashboard')}
 onLogout={logout}
 />
 } 
 />
 
 <Route 
 path="/driver/registration-success"element={
 <DriverRegistrationSuccess
 user={user}
 onHome={() => navigate('/dashboard')}
 onLogout={logout}
 />
 } 
 />
 
 {/* Driver Routes - Dashboard always accessible (component handles registration checks) */}
 <Route 
 path="/driver/dashboard"element={
 <DriverDashboard
 driver={user.driverId ? { driverId: user.driverId, userId: user.id || user._id } : null}
 user={user}
 onBack={() => navigate('/dashboard')}
 onLogout={logout}
 onHome={() => navigate('/dashboard')}
 />
 } 
 />
 
 {/* Other Driver Routes - Only accessible if user is a driver */}
 {user.driverId && (
 <>
 <Route 
 path="/driver/pickup-to-dropoff"element={
 <PickupToDropoff
 onBack={() => navigate('/driver/dashboard')}
 />
 } 
 />
 <Route 
 path="/driver/otp-verification"element={<OTPVerificationPage />} 
 />
 <Route 
 path="/driver/reserve-booking/:bookingId"element={<DriverReserveBookingDetailsPage user={user} onLogout={logout} />} 
 />
 <Route 
 path="/driver/reserve-booking-details"element={<DriverReserveBookingDetailsPage user={user} onLogout={logout} />} 
 />
 </>
 )}
 </>
 )}
 
 {/* ====================================================================== */}
 {/* DEFAULT ROUTES - CATCH-ALL AND REDIRECTS */}
 {/* ====================================================================== */}
 {/* CRITICAL: Only apply catch-all redirects if NOT on shared-ride paths */}
 {/* This ensures deep links are never redirected */}
 {/* Use multiple checks to be absolutely sure */}
 {!isSharedRidePath && !isCurrentlySharedRide && (
 <>
 {user ? (
 <>
 <Route 
 path="/"element={
 (() => {
 // CRITICAL: Never redirect if on shared-ride path
 const currentPath = window.location.pathname;
 const locationPath = location.pathname;
 
 if (currentPath.startsWith('/shared-ride/') || locationPath.startsWith('/shared-ride/')) {
 console.warn('⚠️ Root redirect blocked - on shared-ride path:', currentPath || locationPath);
 return null;
 }
 
 // Only redirect to dashboard if not on special paths
 return <Navigate to="/dashboard"replace />;
 })()
 } 
 />
 <Route 
 path="*"element={(() => {
 // Triple-check we're not on a shared-ride path before redirecting
 const path = window.location.pathname;
 if (path.startsWith('/shared-ride')) {
 console.warn('⚠️⚠️⚠️ Catch-all tried to redirect shared-ride path:', path,'- BLOCKING');
 return null; // Don't redirect - return empty to prevent any navigation
 }
 // Also check location.pathname from hook
 if (location.pathname.startsWith('/shared-ride')) {
 console.warn('⚠️⚠️⚠️ Catch-all tried to redirect shared-ride path (from hook):', location.pathname,'- BLOCKING');
 return null;
 }
 if (path.startsWith('/subscription') || path.startsWith('/pricing') || path.startsWith('/SubscriptionPricingPage')) {
 console.log('🧾 Catch-all mapped subscription-like path to /subscription-pricing');
 return <Navigate to="/subscription-pricing"replace />;
 }
 // CRITICAL: Don't redirect if already on dashboard
 if (path ==='/dashboard'|| location.pathname ==='/dashboard') {
 console.log('⚠️ Already on dashboard, not redirecting');
 return null;
 }
 // CRITICAL: Don't redirect if on /book-ride (especially during refresh)
 // Check if user is on BookRidePage by checking sessionStorage flag
 const isOnBookRidePage = sessionStorage.getItem('is_on_book_ride_page') ==='true';
 if (path ==='/book-ride'|| location.pathname ==='/book-ride'|| isOnBookRidePage) {
 console.log('⚠️ On /book-ride page, not redirecting (refresh protection)');
 return null;
 }
 console.log('🔀 Catch-all redirect to /dashboard for authenticated user');
 return <Navigate to="/dashboard"replace />;
 })()} 
 />
 </>
 ) : (
 <>
 <Route path="/"element={<Navigate to="/"replace />} />
 <Route 
 path="*"element={(() => {
 // Triple-check we're not on a shared-ride path before redirecting
 const path = window.location.pathname;
 if (path.startsWith('/shared-ride')) {
 console.warn('⚠️⚠️⚠️ Catch-all tried to redirect shared-ride path:', path,'- BLOCKING');
 return null; // Don't redirect
 }
 if (location.pathname.startsWith('/shared-ride')) {
 console.warn('⚠️⚠️⚠️ Catch-all tried to redirect shared-ride path (from hook):', location.pathname,'- BLOCKING');
 return null;
 }
 if (path.startsWith('/subscription') || path.startsWith('/pricing') || path.startsWith('/SubscriptionPricingPage')) {
 console.log('🧾 Catch-all mapped subscription-like path to /subscription-pricing (unauthenticated)');
 return <Navigate to="/subscription-pricing"replace />;
 }
 console.log('🔀 Catch-all redirect to / for unauthenticated user');
 return <Navigate to="/"replace />;
 })()} 
 />
 </>
 )}
 </>
 )}
 </Routes>
 </Suspense>
 );
 };

 // CRITICAL: If on shared-ride path, ALWAYS render routes (never show landing page or redirect)
 // This must be checked here after AppRoutes is defined
 if (isSharedRidePath) {
 console.log('🔗🔗🔗 SHARED-RIDE PATH DETECTED - Rendering routes directly (bypassing auth checks)');
 return (
 <>
 <LanguageInitializer />
 <AppRoutes />
 </>
 );
 }

 // CRITICAL: If on /register path, ALWAYS render routes (never show landing page)
 // This ensures register page is accessible without authentication and translation can apply reliably.
 if (isRegisterPath) {
 console.log('🧾🧾🧾 REGISTER PATH DETECTED - Rendering routes directly (bypassing auth checks)');
 return (
 <>
 <LanguageInitializer />
 <AppRoutes />
 </>
 );
 }

 if (isSubscriptionPath) {
 console.log('💎 SUBSCRIPTION PATH DETECTED - Rendering routes directly');
 return (
 <>
 <LanguageInitializer />
 <AppRoutes />
 </>
 );
 }

 // Render unified routes with chatbot
 // Always render AppRoutes so shared-ride routes are accessible
 return (
 <>
 <LanguageInitializer />
 <AppRoutes />
 </>
 );
}

function App() {
 return (
 <ErrorBoundary>
      <Router>
          <AtozasLanguageProvider>
            <AutoTranslateWrapper>
              <AuthProvider>
                <AppContentWrapper />
              </AuthProvider>
            </AutoTranslateWrapper>
          </AtozasLanguageProvider>
        </Router>
 </ErrorBoundary>
 );
}

// Wrapper to access user from AuthContext before passing to NotificationProvider
function AppContentWrapper() {
 const { user } = useAuth();
 const location = useLocation();
 
 // Track which bookings have already played audio to prevent duplicates
 const playedBookingsRef = useRef(new Set());
 
 // Listen for messages from service worker to play audio for booking requests
 useEffect(() => {
 const handleServiceWorkerMessage = (event) => {
 if (event.data && event.data.type ==='PLAY_BOOKING_REQUEST_AUDIO') {
 console.log('🔊 Service Worker requested audio playback:', event.data);
 
 const { isReserveBooking, bookingId } = event.data;
 
 // Only play audio if user is a driver
 if (!user || !user.driverId) {
 console.log('⚠️ Audio playback skipped - user is not a driver');
 return;
 }
 
 // Check if we're on the driver dashboard - if so, skip audio here
 // because DriverDashboard will play it via socket event
 const isOnDriverDashboard = location.pathname ==='/driver/dashboard';
 if (isOnDriverDashboard) {
 console.log('⚠️ Audio playback skipped - driver is on dashboard, socket will handle it');
 return;
 }
 
 // Prevent duplicate playback for the same booking
 if (bookingId && playedBookingsRef.current.has(bookingId)) {
 console.log('⚠️ Audio playback skipped - already played for booking:', bookingId);
 return;
 }
 
 // Mark this booking as played
 if (bookingId) {
 playedBookingsRef.current.add(bookingId);
 // Clean up after 10 seconds to allow re-playing if needed
 setTimeout(() => {
 playedBookingsRef.current.delete(bookingId);
 }, 10000);
 }
 
 try {
 let playCount = 0;
 const maxPlays = isReserveBooking ? 2 : 3; // Reserve booking: 2 times, Regular: 3 times
 const audioPath = isReserveBooking ?'/driver/newreserverequest.mp3':'/driver/newriderequest.mp3';
 
 console.log(`🔊 Playing audio from service worker: ${audioPath}, ${maxPlays} times`);
 
 const playAudio = () => {
 // URL encode the path to handle spaces in filenames
 const encodedPath = encodeURI(audioPath);
 console.log(`🔊 Playing audio: ${audioPath} (encoded: ${encodedPath})`);
 const audio = new Audio(encodedPath);
 audio.volume = 0.7;
 
 // Preload audio for better compatibility
 audio.preload ='auto';
 
 // Handle audio loading
 audio.onloadeddata = () => {
 console.log('✅ Audio loaded successfully');
 };
 
 audio.onended = () => {
 playCount++;
 console.log(`🔊 Audio play ${playCount}/${maxPlays} completed`);
 if (playCount < maxPlays) {
 // Play again after a short delay
 setTimeout(() => {
 playAudio();
 }, 200); // 200ms delay between plays
 }
 };
 
 audio.onerror = (err) => {
 console.error('❌ Audio playback error:', err);
 console.error('❌ Audio path:', audioPath,'Encoded:', encodedPath);
 };
 
 // Try to play audio with better error handling for mobile browsers
 const playPromise = audio.play();
 
 if (playPromise !== undefined) {
 playPromise
 .then(() => {
 console.log('✅ Audio playback started successfully');
 })
 .catch(err => {
 console.error('❌ Ride request audio play failed:', err);
 // On mobile browsers, autoplay might be blocked
 // Try to show a notification or visual indicator
 if (err.name ==='NotAllowedError'|| err.name ==='NotSupportedError') {
 console.warn('⚠️ Audio autoplay blocked by browser. This is common on mobile browsers.');
 // Try to play again after a short delay (user might have interacted)
 setTimeout(() => {
 audio.play().catch(e => {
 console.error('❌ Retry audio play also failed:', e);
 });
 }, 500);
 }
 });
 }
 };
 
 playAudio();
 if (isReserveBooking) {
 console.log('📅 Reserve booking request detected - playing reserve booking audio 2 times');
 } else {
 console.log('🚗 Regular booking request detected - playing regular booking audio 3 times');
 }
 } catch (err) {
 console.error('❌ Ride request sound error:', err);
 }
 }
 };

 // Listen for messages from service worker
 // Use multiple methods to ensure compatibility with web and PWA
 if ('serviceWorker'in navigator) {
 const handleServiceWorkerControllerChange = () => {
 console.log('🔄 Service Worker controller changed, message listener should still be active');
 };

 // Method 1: Listen on navigator.serviceWorker (works in web and PWA)
 // This is the primary method that works in both web browsers and PWA
 navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
 
 // Method 2: Also ensure we're ready (important for PWA)
 // When service worker becomes ready, ensure message listener is active
 navigator.serviceWorker.ready.then((_registration) => {
 console.log('✅ Service Worker ready, message listener active');
 // The addEventListener on navigator.serviceWorker should already be active
 // But we can verify by checking if controller exists
 if (navigator.serviceWorker.controller) {
 console.log('✅ Service Worker controller active');
 }
 }).catch(err => {
 console.error('❌ Service Worker ready check failed:', err);
 });
 
 // Method 3: Listen for controller changes (important when service worker updates)
 // This ensures we continue receiving messages after service worker updates
 navigator.serviceWorker.addEventListener('controllerchange', handleServiceWorkerControllerChange);
 
 return () => {
 navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
 navigator.serviceWorker.removeEventListener('controllerchange', handleServiceWorkerControllerChange);
 };
 }
 }, [user, location.pathname]);
 
 return (
 <NotificationProvider user={user}>
 <AppContent />
 <PWAInstallPrompt />
 </NotificationProvider>
 );
}

export default App;
